import { useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import Head from 'next/head';
import compose from 'lodash/flowRight';
import map from 'lodash/map';
import _includes from 'lodash/includes';
import { graphql } from '@apollo/client/react/hoc';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import MLink from '@material-ui/core/Link';
import red from '@material-ui/core/colors/red';
import MailIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import { makeStyles } from '@material-ui/core/styles';
import ErrorMessage from '../../../components/error-message';
import NotFound from '../../../components/not-found';
import NewComment from '../../../components/events/new-comment';
import CommentList from '../../../components/events/comment-list';
import Host from '../../../components/host';
import AttendeesSummary from '../../../components/events/attendees-summary';
import JoinToggle from '../../../components/events/join-toggle';
import Location from '../../../components/events/location';
import LocationOnline from '../../../components/events/location-online';
import { eventQuery, eventInviteQuery } from '../../../apollo/events';
import { buildTitle } from '../../../utils/layout';
import EventDate from '../../../components/events/date';
import CategoryTags from '../../../components/events/category-tags';
import InfoUrlButton from '../../../components/events/info-url-button';
import MaterialLink from '../../../components/material-link';
import MaterialLinkButton from '../../../components/material-link-button';
import EventHeader from '../../../components/events/header';
import NotificationPreferenceToggle from '../../../components/events/notification-preference-toggle';
import RouteMap from '../../../components/events/route-map';
import EventSeriesUpcoming from '../../../components/events/event-series-upcoming';
import config from '../../../config';
import { momentParse } from '../../../utils/time';
import UrlLink from '../../../components/url-link';
import Markdown from '../../../components/markdown';
import ImageUpload from '../../../components/image-upload';
import { buildImageUrl } from '../../../utils/images';
import { eventTypes } from '../../../utils/events';
import consumeAppContext from '../../../consumers/app';
import { buildJsonLdEvent } from '../../../json-ld/event';
import { getEventCategoryName } from '../../../utils/event-categories';
import Main from '../../../components/main';
import EventImageUploads from '../../../components/events/image-uploads';
import HostSummaries from '../../../components/host-summaries';
import ShareButtons from '../../../components/share-buttons';
import EventEditDropdown from '../../../components/events/edit-dropdown';
import GroupPendingButton from '../../../components/events/group-pending-button';
import HostUser from '../../../components/host-user';
import HostGroup from '../../../components/host-group';

const buildJsonLDBreadcrumbs = (event) => {
  const { group, city } = event;
  const items = [];
  if (group) {
    items.push({
      '@id': `${config().universal.baseUri}/groups/${group.slug}`,
      name: group.name,
    });
  } else if (city) {
    items.push({
      '@id': `${config().universal.baseUri}/cities/${city.slug}`,
      name: city.name,
    });
  }
  items.push({
    '@id': `${config().universal.baseUri}/events/${event.id}`,
    name: event.name,
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: map(items, (item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item
    }))
  };
};

const useStyles = makeStyles((theme) => ({
  eventDate: {
    margin: theme.spacing(0, 0, 2, 0),
  },
  commentsHeader: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'flex-end',
  },
  commentsHeaderText: {
    flexGrow: 1,
  },
  commentList: {
    margin: theme.spacing(2, 0),
  },
  meta: {
    margin: theme.spacing(2, 0),
  },
  metaName: {
    fontWeight: 'bold',
  },
  metaValue: {
    '& > p': {
      margin: 0,
    },
  },
  hostWrapper: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  hostedBy: {
    marginRight: theme.spacing(0.5),
    marginBottom: 2,
  },
  host: {
    '& > *': {
      marginBottom: 2,
    },
  },
  postedIn: {
    margin: theme.spacing(2, 0),
  },
  cancelled: {
    padding: theme.spacing(2),
    border: `2px solid ${red.A400}`,
    '& > h3': {
      marginTop: 0,
    },
  },
  cancelledTime: {
    fontSize: 12,
  },
  main: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  about: {
    flexGrow: 1
  },
  image: {
    maxWidth: 300,
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      maxWidth: '100%',
    },
  },
  editButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  emailLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  emailIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 16,
  },
  rsvp: {
    marginBottom: theme.spacing(2),
  },
  shareButtons: {
    marginBottom: theme.spacing(2.5),
  },
  deniedHostWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  deniedUser: {
    marginRight: theme.spacing(0.5),
  },
  deniedGroup: {
    marginLeft: theme.spacing(0.5),
  },
  deniedMessage: {
    '& > p:last-of-type': {
      marginBottom: 0,
    },
  },
}));

const buildMetaDescription = (event, { momentStartAt }) => {
  let description = '';
  if (momentStartAt) {
    description = description.concat(`${momentStartAt.format('dddd, MMMM D, YYYY')} – `);
  }
  if (event.summary) {
    let summary = event.summary;
    const summaryMax = 158 - description.length;
    if (summary.length > summaryMax) {
      const summaryParts = summary.substring(0, summaryMax).split(' ');
      summary = summaryParts.slice(0, summaryParts.length - 1).join(' ');
    }
    description = description.concat(summary);
  } else {
    const hostName = event.hostType === 'User' ?
      `${event.user.displayName}${event.group ? ` in ${event.group.name}` : ''}` :
      event.group.name
    description.concat(`${event.eventType === 'RIDE' ? 'A ride' : 'An event'} by ${hostName}`);
  }
  return description;
};

const buildMetaKeywords = (event) => {
  const { city } = event;
  let metaKeywords = [];
  if (event.categoryIds) {
    metaKeywords = metaKeywords.concat(map(event.categoryIds, getEventCategoryName));
  }
  metaKeywords.push(city.name);
  if (event.eventType === 'RIDE') {
    metaKeywords = metaKeywords.concat(['bike ride', 'group ride'])
  } else if (event.eventType === 'EVENT') {
    metaKeywords.push('event');
  }
  metaKeywords = metaKeywords.concat(['cycling', 'bicycling']);
  return metaKeywords;
};

const Event = (props) => {
  const {
    loading,
    error,
    event,
    eventInvite,
    eventInviteError,
    eventInviteLoading,
    isLoggedIn,
    refetch,
    router,
    authLoading,
    login
  } = props;
  const classes = useStyles();

  const momentStartAt = useMemo(() => event && event.startAt ? momentParse(event.startAt, event.timezone) : null, [event]);

  useEffect(() => {
    if (event && event.image && event.permissions.editable) {
      new Image().src = buildImageUrl(event.image, 'json-ld');
    }
  }, ['event']);

  if (error || eventInviteError) {
    return (
      <ErrorMessage
        message='An error occurred while loading event'
        error={error || eventInviteError}
      />
    );
  }
  if (loading || eventInviteLoading || authLoading) { return null; } // TODO: show loading
  if (!event) { return <NotFound />; }
  const autoRsvp = router.query.rsvp;

  const { user, group, city, hostType } = event;

  const hasRsvp = event.membership && _includes(['GOING', 'INTERESTED', 'NOT_GOING'], event.membership.status);

  const metaDescription = buildMetaDescription(event, { momentStartAt });
  const cardTitle = event.name;
  const cardUrl = `${config().universal.baseUri}/events/${event.id}`;
  const cardHostName = hostType === 'User' ?
    `${user.displayName}${group ? ` in ${group.name}` : ''}` :
    group.name;
  const cardImage = event.image ? buildImageUrl(event.image, 'facebook-share') : null;
  const cardDate = momentStartAt ? momentStartAt.format('MMMM Do h:mma') : 'TBD';

  return (
    <Fragment>
      <Head>
        <title>{buildTitle(event.name)}</title>
        {(event.visibility !== 'PUBLIC' || event.status !== 'PUBLISHED') && <meta name="robots" content="noindex" />}
        <link rel="canonical" href={`${config().universal.baseUri}/events/${event.id}`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLDBreadcrumbs(event))
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLdEvent(event))
          }}
        />
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={buildMetaKeywords(event).join(',')} />
        <meta property="og:site_name" content="Everyday Rides" />
        <meta property="og:url" content={cardUrl} />
        <meta property="og:title" content={cardTitle} />
        <meta property="og:description" content={metaDescription} />
        {cardImage && (
          <meta property="og:image" content={cardImage} />
        )}
        <meta name="twitter:card" content={cardImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:domain" value="everydayrides.com" />
        <meta name="twitter:title" value={cardTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:url" content={cardUrl} />
        {cardImage && (
          <meta name="twitter:image" content={cardImage} />
        )}
        <meta name="twitter:label1" value="Date" />
        <meta name="twitter:data1" value={cardDate} />
        <meta name="twitter:label2" value="Hosted by" />
        <meta name="twitter:data2" value={cardHostName} />
      </Head>

      <EventSeriesUpcoming event={event} />

      <Main>
        <EventHeader event={event} tab="overview" />

        {event.permissions.accessible && event.status === 'CANCELLED' && (
          <Paper className={classes.cancelled}>
            <Typography variant="h3">Event Cancelled</Typography>
            <Markdown>{event.cancellationMessage}</Markdown>
            {event.cancelledAt && (
              <div className={classes.cancelledTime}>
                Cancelled on {momentParse(event.cancelledAt, event.timezone).format('M/D/YYYY')}
              </div>
            )}
          </Paper>
        )}

        {event.status === 'GROUP_PENDING' && event.group.permissions.editable && (
          <Paper className={classes.cancelled}>
            <Typography variant="h3">Pending Group Approval</Typography>
            <GroupPendingButton event={event} variant="contained" color="primary" />
          </Paper>
        )}

        {event.groupDeniedByUser && (
          <Paper className={classes.cancelled}>
            <Typography variant="h3">Group Approval Denied</Typography>
            <div className={classes.deniedHostWrapper}>
              <HostUser user={event.groupDeniedByUser} simple className={classes.deniedUser} /> denied your event submission to <HostGroup group={event.group} simple className={classes.deniedGroup} />.
            </div>
            <div className={classes.deniedMessage}>
              {event.groupDeniedMessage ? (
                <Markdown>{event.groupDeniedMessage}</Markdown>
              ) : (
                <p><em>No message was given.</em></p>
              )}
            </div>
          </Paper>
        )}

        <div className={classes.main}>
          <div className={classes.about}>
            <div className={classes.rsvp}>
              <JoinToggle
                event={event}
                eventInvite={eventInvite}
                autoRsvp={autoRsvp}
                onRsvp={() => {
                  if (autoRsvp) {
                    router.replace(
                      '/events/[id]',
                      `/events/${event.id}`,
                      { shallow: true }
                    );
                  }
                }}
                onJoin={() => {
                  refetch({ id: event.id });
                }}
              />
              <AttendeesSummary event={event} />
            </div>

            {event.permissions.accessible && (
              <Fragment>
                <EventDate event={event} />

                {event.eventType === 'ONLINE_EVENT' ? (
                  <LocationOnline event={event} />
                ) : (
                  <Fragment>
                    <Location event={event} type="start" />
                    <Location event={event} type="end" />
                  </Fragment>
                )}
              </Fragment>
            )}

            <div className={classes.hostWrapper}>
              <span className={classes.hostedBy}>Hosted by</span> <Host event={event} className={classes.host} />
            </div>

            {event.series && (
              <div className={classes.postedIn}>
                Part of
                {' '}
                <MaterialLink href="/event-series/[id]" as={`/event-series/${event.series.id}`}>
                  {event.series.name} Series
                </MaterialLink>
              </div>
            )}

            {event.visibility === 'PUBLIC' && (
              <div className={classes.postedIn}>
                Posted in
                {' '}
                <MaterialLink href="/cities/[slug]" as={`/cities/${city.slug}`}>
                  {city.name}
                </MaterialLink>
              </div>
            )}

            <ShareButtons
              title={`${event.name}${momentStartAt ? ` on ${momentStartAt.format('ddd, MMMM D, YYYY')}` : ''}`}
              path={`/events/${event.id}`}
              className={classes.shareButtons}
            />

            {(event.permissions.editable || hasRsvp) && (
              <div className={classes.editButtons}>
                {event.permissions.editable && (
                  <Fragment>
                    <EventEditDropdown
                      variant="outlined"
                      event={event}
                    />
                    <MaterialLinkButton
                      href="/events/[id]/attendees"
                      as={`/events/${event.id}/attendees`}
                      variant="outlined"
                    >
                      Manage Attendees
                    </MaterialLinkButton>
                  </Fragment>
                )}
                
                {hasRsvp && (
                  <MaterialLinkButton
                    href="/events/[id]/settings"
                    as={`/events/${event.id}/settings`}
                    variant="outlined"
                  >
                    RSVP Settings
                  </MaterialLinkButton>
                )}
              </div>
            )}
          </div>

          {event.image && (
            <div className={classes.image}>
              <ImageUpload record={event} size="300x300iwe" linkToFull />
            </div>
          )}
        </div>

        {event.permissions.accessible ? (
          <Fragment>
            <Typography variant="h2">Details</Typography>

            <Markdown>{event.description}</Markdown>

            {!event.permissions.approved && !eventInvite && (
              <p><em>{eventTypes[event.eventType].name} details omitted pending approval</em></p>
            )}

            <CategoryTags event={event} />

            <InfoUrlButton event={event} />

            {event.routeUrl && (
              <div className={classes.meta}>
                <div className={classes.metaName}>
                  Route
                </div>
                <div className={classes.metaValue}>
                  <UrlLink url={event.routeUrl} raw />
                </div>
                <RouteMap event={event} />
              </div>
            )}

            {(event.contactName || event.contactPhoneNumberDisplay || event.contactEmail) && (
              <div className={classes.meta}>
                <div className={classes.metaName}>
                  Contact
                </div>
                {event.contactName && (
                  <div className={classes.metaValue}>
                    {event.contactName}
                  </div>
                )}
                {event.contactEmail && (
                  <div>
                    <MLink href={`mailto:${event.contactEmail}`} className={classes.emailLink}>
                      <MailIcon className={classes.emailIcon} />
                      {event.contactEmail}
                    </MLink>
                  </div>
                )}
                {event.contactPhoneNumber && (
                  <div>
                    <MLink href={event.contactPhoneNumberUri} className={classes.emailLink}>
                      <PhoneIcon className={classes.emailIcon} />
                      {event.contactPhoneNumberDisplay}
                    </MLink>
                  </div>
                )}
              </div>
            )}

            <HostSummaries event={event} />

            {event.permissions.approved && !eventInvite && (
              <Fragment>
                <EventImageUploads event={event} id="discussion" />

                <Typography variant="h2" className={classes.commentsHeader}>
                  <div className={classes.commentsHeaderText}>Discussion</div>
                  <div className={classes.notificationPreferenceToggle}>
                    <NotificationPreferenceToggle event={event} />
                  </div>
                </Typography>
                <div className={classes.commentList}>
                  <CommentList event={event} />
                </div>
                <div className={classes.newComment}>
                  <NewComment event={event} />
                </div>
              </Fragment>
            )}
          </Fragment>
        ) : (
          <Fragment>
            {group ? (
              <p>Only members of {group.name} can access this event.</p>
            ) : (
              <p>You do not have access to this event.</p>
            )}
            {!isLoggedIn && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  login(autoRsvp ? `/events/${event.id}?rsvp=${autoRsvp}` : `/events/${event.id}`);
                }}
              >
                Log In
              </Button>
            )}
          </Fragment>
        )}
      </Main>
    </Fragment>
  );
}

Event.propTypes = {
  router: PropTypes.object,
  event: PropTypes.object,
  error: PropTypes.object,
  loading: PropTypes.bool,
  eventInvite: PropTypes.object,
  eventInviteError: PropTypes.object,
  eventInviteLoading: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  authLoading: PropTypes.bool,
  refetch: PropTypes.func,
  login: PropTypes.func
};

export default compose(
  withRouter,
  consumeAppContext,
  graphql(eventQuery, {
    props: ({ data: { event, loading, error, refetch } }) => {
      return {
        event,
        loading,
        error,
        refetch
      };
    },
    options: ({ router }) => ({
      variables: {
        id: router.query.id,
        inviteToken: router.query.join
      }
    })
  }),
  graphql(eventInviteQuery, {
    props: ({ data: { eventInvite, loading, error } }) => {
      return {
        eventInvite,
        eventInviteLoading: loading,
        eventInviteError: error
      };
    },
    options: ({ router }) => ({
      variables: {
        id: router.query.join
      }
    }),
    skip: ({ router }) => !router.query.join
  }),
)(Event);
